import React, { useState, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {  closeAuth, setSignupData, setSigninPassword } from '../../redux/slice/authModalSlice';
import { Images } from '../../utils/imageConstants';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import toast from "react-hot-toast";
import { FaCamera } from 'react-icons/fa';
import FormDropdown, { type FormDropdownOption } from '../../components/common/FormDropdown';
import { ProfileDatePicker } from '../profileOnboarding/components/ProfileDatePicker';
import { BowlingStyle, BattingStyle, PlayerRole, PaymentFrequency, Gender, PlayerProfileStatus, RelationType } from '../../types/profile';
import { formatEnumLabel } from '../../utils/stringUtils';
import { fileToBase64, splitBase64 } from '../../utils/fileUtils';
import { Api } from '../../services/interceptor';
import '../../styles/signup/signup.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchUserProfile } from '../profile/api/userProfileApi';
import { setProfile } from '../../redux/slice/playerProfileSlice';
import { resolvePostAuthRedirect } from '../../helper/helper';


const schema = yup.object().shape({
  // Step 1: Personal
  firstName: yup.string().required('First Name is required'),
  middleName: yup.string(),
  lastName: yup.string().required('Last Name is required'),
  dateOfBirth: yup.string().required('Date of Birth is required'),
  mobileNumber: yup.string().required('Mobile Number is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  gender: yup.string().oneOf(Object.values(Gender)).required('Gender is required'),
  registerAsPlayer: yup.boolean(),
  registerAsUmpire: yup.boolean(),
  registerAsScorer: yup.boolean(),
  registerAsStreamer: yup.boolean(),
  
  // Step 2: Player Details
  battingStyle: yup.string().when('registerAsPlayer', { is: true, then: (s) => s.oneOf(Object.values(BattingStyle)).required('Batting style is required') }),
  bowlingStyle: yup.string().when('registerAsPlayer', { is: true, then: (s) => s.oneOf(Object.values(BowlingStyle)).required('Bowling style is required') }),
  playingRole: yup.string().when('registerAsPlayer', { is: true, then: (s) => s.oneOf(Object.values(PlayerRole)).required('Playing role is required') }),
  
  // Step 3: Umpire Details
  umpireExperience: yup.number().typeError('Must be a number').when('registerAsUmpire', { is: true, then: (s) => s.required('Experience is required') }),
  umpireFees: yup.number().typeError('Must be a number').when('registerAsUmpire', { is: true, then: (s) => s.required('Fees are required') }),
  umpireFrequency: yup.string().when('registerAsUmpire', { is: true, then: (s) => s.oneOf(Object.values(PaymentFrequency)).required('Frequency is required') }),
  umpireLocations: yup.string().when('registerAsUmpire', { is: true, then: (s) => s.required('Locations are required') }),
  umpireCurrency: yup.string().when('registerAsUmpire', { is: true, then: (s) => s.required('Currency is required') }),

  // Step 4: Scorer Details
  scorerExperienceYears: yup.number().typeError('Must be a number').when('registerAsScorer', { is: true, then: (s) => s.required('Experience is required') }),
  scorerCity: yup.string().when('registerAsScorer', { is: true, then: (s) => s.required('City is required') }),
  familiarScoringMethods: yup.string().when('registerAsScorer', { is: true, then: (s) => s.required('Scoring methods are required') }),
  scoringInterfacePreferred: yup.string().when('registerAsScorer', { is: true, then: (s) => s.required('Preferred interface is required') }),
  scorerFeesPerMatch: yup.number().typeError('Must be a number').when('registerAsScorer', { is: true, then: (s) => s.required('Fees per match are required') }),
  scorerFeesPerDay: yup.number().typeError('Must be a number').when('registerAsScorer', { is: true, then: (s) => s.required('Fees per day are required') }),
  scorerLanguagesKnown: yup.string().when('registerAsScorer', { is: true, then: (s) => s.required('Languages are required') }),
  scorerBio: yup.string().when('registerAsScorer', { is: true, then: (s) => s.required('Bio is required') }),

  // Step 5: Streamer Details
  cameraSetup: yup.string().when('registerAsStreamer', { is: true, then: (s) => s.required('Camera setup is required') }),
  streamingDevice: yup.string().when('registerAsStreamer', { is: true, then: (s) => s.required('Streaming device is required') }),
  audioSetup: yup.string().when('registerAsStreamer', { is: true, then: (s) => s.required('Audio setup is required') }),
  streamingPlatforms: yup.string().when('registerAsStreamer', { is: true, then: (s) => s.required('Streaming platforms are required') }),
  pricingPerMatch: yup.number().typeError('Must be a number').when('registerAsStreamer', { is: true, then: (s) => s.required('Pricing per match is required') }),
  pricingPerDay: yup.number().typeError('Must be a number').when('registerAsStreamer', { is: true, then: (s) => s.required('Pricing per day is required') }),
  availableForPaidStreaming: yup.boolean(),
  sampleStreamLink: yup.string().url('Invalid URL').when('registerAsStreamer', { is: true, then: (s) => s.required('Stream link is required') }),

  // Step 6: Other Info
  emergencyName: yup.string().required('Emergency name is required'),
  emergencyContactRelation: yup.string().oneOf(Object.values(RelationType)).required('Relation is required'),
  emergencyNo: yup.string().required('Emergency number is required'),
  zipCode: yup.string().required('Zip code is required'),
  state: yup.string().required('State is required'),
  stAddress: yup.string().max(150, 'Max 150 characters').required('Address is required'),
  desc: yup.string().max(150, 'Max 150 characters'),
  termsAccepted: yup.boolean().oneOf([true], 'You must accept the terms'),
});

type ProfileSetupFormValues = any; // Simplifying for multi-step logic

// Multi-select dropdown options (matching UserProfilePage Roles & Capabilities tab)
const SCORER_OPTIONS = {
  scoringInterfacePreferred: ["Mobile", "Tablet", "Desktop"],
  familiarScoringMethods: ["Linear", "Manual", "Digital"],
  scorerLanguagesKnown: ["English", "Hindi", "Tamil", "Telugu", "Kannada", "Bengali", "Marathi"],
};

const STREAMER_OPTIONS = {
  cameraSetup: ["Mobile", "DSLR", "Multi-Camera"],
  streamingDevice: ["Mobile", "Laptop", "PC"],
  audioSetup: ["No mic", "Basic mic", "Professional mic"],
  streamingPlatforms: ["Youtube", "Facebook"],
};

const playingRoleDropdownOptions: FormDropdownOption[] = [
  { value: '', label: 'Select Role' },
  ...Object.values(PlayerRole).map((v) => ({ value: v, label: formatEnumLabel(v) })),
];

const battingStyleDropdownOptions: FormDropdownOption[] = [
  { value: '', label: 'Select Style' },
  ...Object.values(BattingStyle).map((v) => ({ value: v, label: formatEnumLabel(v) })),
];

const bowlingStyleDropdownOptions: FormDropdownOption[] = [
  { value: '', label: 'Select Style' },
  ...Object.values(BowlingStyle).map((v) => ({ value: v, label: formatEnumLabel(v) })),
];

const umpireCurrencyDropdownOptions: FormDropdownOption[] = [
  { value: 'INR', label: 'INR' },
  { value: 'USD', label: 'USD' },
  { value: 'GBP', label: 'GBP' },
];

const umpireFrequencyDropdownOptions: FormDropdownOption[] = [
  { value: '', label: 'Select Frequency' },
  ...Object.values(PaymentFrequency).map((f) => ({ value: f, label: formatEnumLabel(f) })),
];

const relationDropdownOptions: FormDropdownOption[] = [
  { value: '', label: 'Select relation' },
  ...Object.values(RelationType).map((r) => ({ value: r, label: formatEnumLabel(r) })),
];

const ProfileSetup: React.FC = () => {
  const dispatch = useAppDispatch();
  const signupEmail = useAppSelector((s) => s.authModal.signupEmail);
  const signupPhone = useAppSelector((s) => s.authModal.signupMobileNumber);
  const playerId = useAppSelector((s) => s.authModal.playerId);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Multi-select dropdown state
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [tempSelections, setTempSelections] = useState<Record<string, string[]>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (fieldName: string) => {
    if (openDropdown === fieldName) {
      setOpenDropdown(null);
    } else {
      const currentVal = watch(fieldName) as string;
      setTempSelections(prev => ({
        ...prev,
        [fieldName]: currentVal ? currentVal.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
      }));
      setOpenDropdown(fieldName);
    }
  };

  const handleTempToggle = (fieldName: string, option: string) => {
    setTempSelections(prev => {
      const current = prev[fieldName] || [];
      return {
        ...prev,
        [fieldName]: current.includes(option) ? current.filter(v => v !== option) : [...current, option],
      };
    });
  };

  const applySelections = (fieldName: string) => {
    const selected = tempSelections[fieldName] || [];
    setValue(fieldName, selected.join(', '), { shouldValidate: true });
    setOpenDropdown(null);
  };

  const clearSelections = (fieldName: string) => {
    setTempSelections(prev => ({ ...prev, [fieldName]: [] }));
  };

  const renderMultiSelect = (fieldName: string, label: string, options: string[], required = true) => {
    const currentVal = watch(fieldName) as string;
    const isOpen = openDropdown === fieldName;
    const selected = tempSelections[fieldName] || [];

    return (
      <div className="form-group" style={{ position: 'relative' }}>
        <label className="form-label">{label}{required && <span className="required-star">*</span>}</label>
        <div
          className={`form-input ${(errors as any)[fieldName] ? 'error' : ''}`}
          onClick={() => toggleDropdown(fieldName)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '42px', userSelect: 'none' }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: currentVal ? '#1e293b' : '#94a3b8' }}>
            {currentVal || `Select ${label}`}
          </span>
          <span style={{ fontSize: '10px', marginLeft: '8px', flexShrink: 0 }}>&#9660;</span>
        </div>
        <input type="hidden" {...register(fieldName)} />
        {isOpen && (
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: '4px', padding: '8px 0',
            }}
          >
            {options.map(option => (
              <label
                key={option}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', cursor: 'pointer', fontSize: '14px' }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => handleTempToggle(fieldName, option)}
                  style={{ width: '16px', height: '16px', accentColor: '#6366f1' }}
                />
                {option}
              </label>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '8px 14px', borderTop: '1px solid #f1f5f9', marginTop: '4px' }}>
              <button type="button" onClick={() => clearSelections(fieldName)} style={{ fontSize: '13px', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>Clear All</button>
              <button type="button" onClick={() => applySelections(fieldName)} style={{ fontSize: '13px', color: '#fff', background: '#6366f1', border: 'none', borderRadius: '6px', padding: '4px 16px', cursor: 'pointer' }}>Apply</button>
            </div>
          </div>
        )}
        {(errors as any)[fieldName]?.message && <span className="error-message">{String((errors as any)[fieldName].message)}</span>}
      </div>
    );
  };

  const { register, handleSubmit, control, formState: { errors, isValid }, watch, trigger, setValue } = useForm<ProfileSetupFormValues>({
    resolver: yupResolver(schema) as any,
    mode: 'onTouched',
    defaultValues: {
      email: signupEmail || '',
      firstName: '',
      middleName: '',
      lastName: '',
      dateOfBirth: '',
      mobileNumber: signupPhone || '',
      gender: undefined,
      registerAsPlayer: false,
      registerAsUmpire: false,
      registerAsScorer: false,
      registerAsStreamer: false,
      // Umpire
      umpireCurrency: 'INR',
      umpireLocations: '',
      // Scorer
      familiarScoringMethods: '',
      matchFormatsWorkedOn: '',
      scoringInterfacePreferred: '',
      scorerLanguagesKnown: '',
      // Streamer
      streamingPlatforms: '',
      availableForPaidStreaming: true,
      // Other Info
      emergencyName: '',
      emergencyNo: '',
      emergencyContactRelation: '',
      zipCode: '',
      state: '',
      stAddress: '',
      desc: '',
      termsAccepted: false,
    },
  });
 
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
 
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };
 
  const mapFormToRequest = async (values: ProfileSetupFormValues, status: PlayerProfileStatus) => {
    let imageData = undefined;
    if (profileImage) {
      const base64 = await fileToBase64(profileImage);
      const { type, data } = splitBase64(base64);
      imageData = {
        fileType: type,
        base64Image: data
      };
    }
 
    return {
      playerId: playerId,
      playerFirstName: values.firstName,
      playerMiddleName: values.middleName,
      playerLastName: values.lastName,
      name: `${values.firstName} ${values.middleName ? values.middleName + ' ' : ''}${values.lastName}`,
      gender: values.gender,
      DOB: values.dateOfBirth,
      emailId: values.email,
      phoneNo: values.mobileNumber,
      countryCode: 91, // Defaulting to India as per phone input
      playerRole: values.registerAsPlayer ? values.playingRole : undefined,
      battingStyle: values.battingStyle,
      bowlingStyle: values.bowlingStyle,
      
      isUmpire: values.registerAsUmpire,
      experience: values.registerAsUmpire ? Number(values.umpireExperience) : undefined,
      fees: values.registerAsUmpire ? Number(values.umpireFees) : undefined,
      frequency: values.registerAsUmpire ? values.umpireFrequency : undefined,
      locations: values.registerAsUmpire ? values.umpireLocations.split(',').map((s: string) => s.trim()) : undefined,
      currency: values.umpireCurrency,
 
      isScorer: values.registerAsScorer,
      scorerDetails: values.registerAsScorer ? {
        scorerId: playerId,
        experienceYears: Number(values.scorerExperienceYears),
        city: values.scorerCity,
        familiarScoringMethods: values.familiarScoringMethods.split(',').map((s: string) => s.trim()),
        scoringInterfacePreferred: values.scoringInterfacePreferred.split(',').map((s: string) => s.trim()),
        feesPerMatch: Number(values.scorerFeesPerMatch),
        feesPerDay: Number(values.scorerFeesPerDay),
        languagesKnown: values.scorerLanguagesKnown.split(',').map((s: string) => s.trim()),
        bio: values.scorerBio
      } : undefined,
 
      isStreamer: values.registerAsStreamer,
      streamerDetails: values.registerAsStreamer ? {
        streamerId: playerId,
        cameraSetup: values.cameraSetup.split(',').map((s: string) => s.trim()),
        streamingDevice: values.streamingDevice.split(',').map((s: string) => s.trim()),
        audioSetup: values.audioSetup.split(',').map((s: string) => s.trim()),
        streamingPlatforms: values.streamingPlatforms.split(',').map((s: string) => s.trim()),
        pricingPerMatch: Number(values.pricingPerMatch),
        pricingPerDay: Number(values.pricingPerDay),
        availableForPaidStreaming: values.availableForPaidStreaming,
        sampleStreamLink: values.sampleStreamLink
      } : undefined,
 
      emergencyName: values.emergencyName,
      emergencyNo: values.emergencyNo,
      emergencyContactRelation: values.emergencyContactRelation,
      stAddress: values.stAddress,
      zipCode: values.zipCode,
      state: values.state,
      desc: values.desc,
      
      playerProfileStatus: status,
      image: imageData
    };
  };


  const registerAsPlayer = watch('registerAsPlayer');
  const registerAsUmpire = watch('registerAsUmpire');
  const registerAsScorer = watch('registerAsScorer');
  const registerAsStreamer = watch('registerAsStreamer');

  const steps = [
    { id: 'personal', title: 'Personal' },
    ...(registerAsPlayer ? [{ id: 'player', title: 'Player' }] : []),
    ...(registerAsUmpire ? [{ id: 'umpire', title: 'Umpire' }] : []),
    ...(registerAsScorer ? [{ id: 'scorer', title: 'Scorer' }] : []),
    ...(registerAsStreamer ? [{ id: 'streamer', title: 'Streamer' }] : []),
    { id: 'other-info', title: 'Other Info' },
  ];

  // Clamp step index if steps shrink (e.g. user untoggled a role)
  const safeStepIndex = Math.min(currentStepIndex, steps.length - 1);
  const currentStep = steps[safeStepIndex];

  // Keep step index in bounds when steps change
  useEffect(() => {
    if (currentStepIndex >= steps.length) {
      setCurrentStepIndex(steps.length - 1);
    }
  }, [steps.length, currentStepIndex]);

  const goToStep = (index: number) => {
    if (index < safeStepIndex) {
      setCurrentStepIndex(index);
    }
  };

  const nextStep = async () => {
    const fieldsToValidate: any[] = [];
    if (currentStep.id === 'personal') {
      fieldsToValidate.push('firstName', 'lastName', 'dateOfBirth', 'mobileNumber', 'email', 'gender');
    } else if (currentStep.id === 'player') {
      fieldsToValidate.push('battingStyle', 'bowlingStyle', 'playingRole');
    } else if (currentStep.id === 'umpire') {
      fieldsToValidate.push('umpireExperience', 'umpireFees', 'umpireFrequency', 'umpireLocations', 'umpireCurrency');
    } else if (currentStep.id === 'scorer') {
      fieldsToValidate.push('scorerExperienceYears', 'scorerCity', 'familiarScoringMethods', 'matchFormatsWorkedOn', 'scoringInterfacePreferred', 'scorerFeesPerMatch', 'scorerFeesPerDay', 'scorerLanguagesKnown', 'scorerBio');
    } else if (currentStep.id === 'streamer') {
      fieldsToValidate.push('cameraSetup', 'streamingDevice', 'audioSetup', 'streamingPlatforms', 'pricingPerMatch', 'pricingPerDay', 'sampleStreamLink');
    } else if (currentStep.id === 'other-info') {
      fieldsToValidate.push('emergencyName', 'emergencyContactRelation', 'emergencyNo', 'zipCode', 'state', 'stAddress', 'desc', 'termsAccepted');
    }

    const isStepValid = await trigger(fieldsToValidate);
    if (isStepValid) {
      setCurrentStepIndex(safeStepIndex + 1);
    } else {
      toast.error('Please fill all required fields');
    }
  };

  const prevStep = () => setCurrentStepIndex(safeStepIndex - 1);

  const onSubmit = async (values: ProfileSetupFormValues) => {
    setIsSubmitting(true);
    try {
      const payload = await mapFormToRequest(values, PlayerProfileStatus.COMPLETED);
      console.log('Final Profile Payload:', payload);
      await Api.post('/player', payload);
      toast.success('Successfully registered!');
      dispatch(setSignupData(undefined));
      dispatch(setSigninPassword(undefined));
      dispatch(closeAuth());
      try{
        const data = await fetchUserProfile();
        dispatch(setProfile(data));
      }catch(err){
        console.warn("Failed to load user profile");
      } // Close the auth modal
      navigate(resolvePostAuthRedirect(location.search), { replace: true });
    } catch (err) {
      console.error(err);
      toast.error('Failed to complete setup');
    } finally {
      setIsSubmitting(false);
    }
  };
 
  const handleCompleteLater = async () => {
    const values = watch();
    setIsSubmitting(true);
    try {
      const payload = await mapFormToRequest(values, PlayerProfileStatus.PARTIALLY_UPDATED);
      console.log('Partial Profile Payload:', payload);
      await Api.post('/player', payload);
      toast.success('Profile saved, you can complete it later!');
      dispatch(setSignupData(undefined));
      dispatch(setSigninPassword(undefined));
      dispatch(closeAuth());
      try{
        const data = await fetchUserProfile();
        dispatch(setProfile(data));
      }catch(err){
        console.warn("Failed to load user profile");
      }
      navigate(resolvePostAuthRedirect(location.search), { replace: true });
    } catch (err) {
      console.error(err);
      toast.error('Failed to save progress');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="figma-login-container" style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <div className="login-background-image" style={{ minHeight: '100vh', padding: '40px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}>
        
        <div className="profile-setup-header">
          <div className="logo-container">
            <img src={Images.main_logo} alt="CRICCENTER" className="logo" />
          </div>
          <h1 className="app-name">CRICCENTER</h1>
        </div>

        <div className="profile-setup-container">
          {/* Stepper */}
          <div className="stepper-container">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div 
                  className={`step-item ${index === safeStepIndex ? 'active' : index < safeStepIndex ? 'completed' : 'inactive'}`}
                  onClick={() => goToStep(index)}
                >
                  <div className="step-circle">{index + 1}</div>
                  <span className="step-label">{step.title}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className="step-divider"></div>
                )}
              </React.Fragment>
            ))}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="login-form">
            {currentStep.id === 'personal' && (
              <>
                <div className="profile-pic-section">
                  <div className="avatar-container">
                    <img src={imagePreview || Images.dummy_profile} alt="Avatar" className="avatar-img" />
                    <label className="camera-btn">
                      <FaCamera size={14} />
                      <input type="file" style={{ display: 'none' }} accept="image/*" onChange={handleImageChange} />
                    </label>
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">First Name<span className="required-star">*</span></label>
                    <input className={`form-input ${errors.firstName ? 'error' : ''}`} {...register('firstName')} placeholder="Enter first name" />
                    {errors.firstName?.message && <span className="error-message">{String(errors.firstName.message)}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Middle Name</label>
                    <input className="form-input" {...register('middleName')} placeholder="Enter middle name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name<span className="required-star">*</span></label>
                    <input className={`form-input ${errors.lastName ? 'error' : ''}`} {...register('lastName')} placeholder="Enter last name" />
                    {errors.lastName?.message && <span className="error-message">{String(errors.lastName.message)}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date of Birth<span className="required-star">*</span></label>
                    <Controller
                      name="dateOfBirth"
                      control={control}
                      render={({ field }) => (
                        <ProfileDatePicker
                          id="profile-setup-dob"
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          placeholder="Select date of birth"
                          hasError={!!errors.dateOfBirth}
                          disableFuture
                        />
                      )}
                    />
                    {errors.dateOfBirth?.message && <span className="error-message">{String(errors.dateOfBirth.message)}</span>}
                  </div>
                  <div className="form-group">
                    <div className="form-control-wrap create-league-phone-field">
                      <div className="form-group">
                        <label className="form-label">Mobile Number<span className="required-star">*</span></label>
                        <div className="phone">
                          <Controller
                            name="mobileNumber"
                            control={control}
                            render={({ field }) => (
                              <PhoneInput
                                disabled
                                country="in"
                                value={field.value ?? ''}
                                countryCodeEditable={false}
                                onChange={field.onChange}
                                inputClass={`form-control phone-input-field${errors.mobileNumber ? ' field-error' : ''}`}
                                containerClass="phone-input-container"
                                placeholder="Enter contact number"
                                enableSearch
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                    {errors.mobileNumber?.message && <span className="error-message">{String(errors.mobileNumber.message)}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email<span className="required-star">*</span></label>
                    <input disabled className="form-input" {...register('email')} style={{ background: '#f8fafc', color: '#64748b' }} />
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '30px' }}>
                  <label className="form-label">Gender<span className="required-star">*</span></label>
                  <div className="gender-group">
                    {Object.values(Gender).map(g => (
                      <button 
                        key={g} 
                        type="button"
                        className={`gender-btn ${watch('gender') === g ? 'selected' : ''}`}
                        onClick={() => setValue('gender', g, { shouldValidate: true })}
                      >
                        {formatEnumLabel(g)}
                      </button>
                    ))}
                    <input type="hidden" {...register('gender')} />
                  </div>
                  {errors.gender?.message && <span className="error-message">{String(errors.gender.message)}</span>}
                </div>

                <div className="role-toggles">
                  {[
                    { id: 'registerAsPlayer', label: 'Register as a Player', desc: 'If you\'re registered as Player, you will be shown as Player.' },
                    { id: 'registerAsUmpire', label: 'Register as an Umpire', desc: 'If you\'re registered as Umpire, you will be shown as Umpire.' },
                    { id: 'registerAsScorer', label: 'Register as a Scorer', desc: 'If you\'re registered as Scorer, you will be shown as Scorer.' },
                    { id: 'registerAsStreamer', label: 'Register as a Streamer', desc: 'If you\'re registered as Streamer, you will be shown as Streamer.' },
                  ].map(role => (
                    <div key={role.id} className="role-toggle-item">
                      <div className="role-info">
                        <p>{role.label}</p>
                        <p>{role.desc}</p>
                      </div>
                      <label className="switch">
                        <input type="checkbox" {...register(role.id as any)} />
                        <span className="slider"></span>
                      </label>
                    </div>
                  ))}
                </div>
              </>
            )}

            {currentStep.id === 'player' && (
              <div className="form-grid single-column">
                <div className="form-group">
                  <label className="form-label">Playing Role<span className="required-star">*</span></label>
                  <Controller
                    name="playingRole"
                    control={control}
                    render={({ field }) => (
                      <FormDropdown
                        id="profile-setup-playing-role"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        options={playingRoleDropdownOptions}
                        placeholder="Select Role"
                        hasError={!!errors.playingRole}
                      />
                    )}
                  />
                  {errors.playingRole?.message && <span className="error-message">{String(errors.playingRole.message)}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Batting Style<span className="required-star">*</span></label>
                  <Controller
                    name="battingStyle"
                    control={control}
                    render={({ field }) => (
                      <FormDropdown
                        id="profile-setup-batting-style"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        options={battingStyleDropdownOptions}
                        placeholder="Select Style"
                        hasError={!!errors.battingStyle}
                      />
                    )}
                  />
                  {errors.battingStyle?.message && <span className="error-message">{String(errors.battingStyle.message)}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Bowling Style<span className="required-star">*</span></label>
                  <Controller
                    name="bowlingStyle"
                    control={control}
                    render={({ field }) => (
                      <FormDropdown
                        id="profile-setup-bowling-style"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        options={bowlingStyleDropdownOptions}
                        placeholder="Select Style"
                        hasError={!!errors.bowlingStyle}
                      />
                    )}
                  />
                  {errors.bowlingStyle?.message && <span className="error-message">{String(errors.bowlingStyle.message)}</span>}
                </div>
              </div>
            )}

            {currentStep.id === 'umpire' && (
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Experience (Years)<span className="required-star">*</span></label>
                  <input type="number" step="0.5" className={`form-input ${errors.umpireExperience ? 'error' : ''}`} {...register('umpireExperience')} placeholder="e.g. 5.5" />
                  {errors.umpireExperience?.message && <span className="error-message">{String(errors.umpireExperience.message)}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Currency<span className="required-star">*</span></label>
                  <Controller
                    name="umpireCurrency"
                    control={control}
                    render={({ field }) => (
                      <FormDropdown
                        id="profile-setup-umpire-currency"
                        value={field.value ?? 'INR'}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        options={umpireCurrencyDropdownOptions}
                        placeholder="Select currency"
                        hasError={!!errors.umpireCurrency}
                      />
                    )}
                  />
                  {errors.umpireCurrency?.message && <span className="error-message">{String(errors.umpireCurrency.message)}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Fees<span className="required-star">*</span></label>
                  <input type="number" className={`form-input ${errors.umpireFees ? 'error' : ''}`} {...register('umpireFees')} placeholder="Enter fees" />
                  {errors.umpireFees?.message && <span className="error-message">{String(errors.umpireFees.message)}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Frequency<span className="required-star">*</span></label>
                  <Controller
                    name="umpireFrequency"
                    control={control}
                    render={({ field }) => (
                      <FormDropdown
                        id="profile-setup-umpire-frequency"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        options={umpireFrequencyDropdownOptions}
                        placeholder="Select Frequency"
                        hasError={!!errors.umpireFrequency}
                      />
                    )}
                  />
                  {errors.umpireFrequency?.message && <span className="error-message">{String(errors.umpireFrequency.message)}</span>}
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Locations (Comma separated)<span className="required-star">*</span></label>
                  <input className={`form-input ${errors.umpireLocations ? 'error' : ''}`} {...register('umpireLocations')} placeholder="e.g. Mumbai, Navi Mumbai, Thane" />
                  {errors.umpireLocations?.message && <span className="error-message">{String(errors.umpireLocations.message)}</span>}
                </div>
              </div>
            )}
 
            {currentStep.id === 'scorer' && (
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Experience (Years)<span className="required-star">*</span></label>
                  <input type="number" className={`form-input ${errors.scorerExperienceYears ? 'error' : ''}`} {...register('scorerExperienceYears')} placeholder="0" />
                  {errors.scorerExperienceYears?.message && <span className="error-message">{String(errors.scorerExperienceYears.message)}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">City<span className="required-star">*</span></label>
                  <input className={`form-input ${errors.scorerCity ? 'error' : ''}`} {...register('scorerCity')} placeholder="Enter city" />
                  {errors.scorerCity?.message && <span className="error-message">{String(errors.scorerCity.message)}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Fees Per Match<span className="required-star">*</span></label>
                  <input type="number" className={`form-input ${errors.scorerFeesPerMatch ? 'error' : ''}`} {...register('scorerFeesPerMatch')} />
                  {errors.scorerFeesPerMatch?.message && <span className="error-message">{String(errors.scorerFeesPerMatch.message)}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Fees Per Day<span className="required-star">*</span></label>
                  <input type="number" className={`form-input ${errors.scorerFeesPerDay ? 'error' : ''}`} {...register('scorerFeesPerDay')} />
                  {errors.scorerFeesPerDay?.message && <span className="error-message">{String(errors.scorerFeesPerDay.message)}</span>}
                </div>
                {renderMultiSelect('familiarScoringMethods', 'Familiar Scoring Methods', SCORER_OPTIONS.familiarScoringMethods)}
                {renderMultiSelect('scoringInterfacePreferred', 'Preferred Interface', SCORER_OPTIONS.scoringInterfacePreferred)}
                {renderMultiSelect('scorerLanguagesKnown', 'Languages Known', SCORER_OPTIONS.scorerLanguagesKnown)}
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Bio<span className="required-star">*</span></label>
                  <textarea className={`form-input ${errors.scorerBio ? 'error' : ''}`} {...register('scorerBio')} rows={3} placeholder="Tell us about your experience..." style={{ height: 'auto' }} />
                  {errors.scorerBio?.message && <span className="error-message">{String(errors.scorerBio.message)}</span>}
                </div>
              </div>
            )}
 
            {currentStep.id === 'streamer' && (
              <div className="form-grid">
                {renderMultiSelect('cameraSetup', 'Camera Setup', STREAMER_OPTIONS.cameraSetup)}
                {renderMultiSelect('streamingDevice', 'Streaming Device', STREAMER_OPTIONS.streamingDevice)}
                {renderMultiSelect('audioSetup', 'Audio Setup', STREAMER_OPTIONS.audioSetup)}
                {renderMultiSelect('streamingPlatforms', 'Streaming Platforms', STREAMER_OPTIONS.streamingPlatforms)}
                <div className="form-group">
                  <label className="form-label">Pricing Per Match<span className="required-star">*</span></label>
                  <input type="number" className={`form-input ${errors.pricingPerMatch ? 'error' : ''}`} {...register('pricingPerMatch')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Pricing Per Day<span className="required-star">*</span></label>
                  <input type="number" className={`form-input ${errors.pricingPerDay ? 'error' : ''}`} {...register('pricingPerDay')} />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Sample Stream Link<span className="required-star">*</span></label>
                  <input className={`form-input ${errors.sampleStreamLink ? 'error' : ''}`} {...register('sampleStreamLink')} placeholder="https://..." />
                  {errors.sampleStreamLink?.message && <span className="error-message">{String(errors.sampleStreamLink.message)}</span>}
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type="checkbox" {...register('availableForPaidStreaming')} id="availableForPaid" style={{ width: '20px', height: '20px' }} />
                  <label htmlFor="availableForPaid" className="form-label" style={{ marginBottom: 0 }}>Available for Paid Streaming</label>
                </div>
              </div>
            )}

            {currentStep.id === 'other-info' && (
              <div className="form-grid">
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Emergency Contact Name<span className="required-star">*</span></label>
                  <input className={`form-input ${errors.emergencyName ? 'error' : ''}`} {...register('emergencyName')} placeholder="Enter contact name" />
                  {errors.emergencyName?.message && <span className="error-message">{String(errors.emergencyName.message)}</span>}
                </div>
                
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Relation<span className="required-star">*</span></label>
                  <Controller
                    name="emergencyContactRelation"
                    control={control}
                    render={({ field }) => (
                      <FormDropdown
                        id="profile-setup-emergency-relation"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        options={relationDropdownOptions}
                        placeholder="Select relation"
                        hasError={!!errors.emergencyContactRelation}
                      />
                    )}
                  />
                  {errors.emergencyContactRelation?.message && <span className="error-message">{String(errors.emergencyContactRelation.message)}</span>}
                </div>
 
                <div className="form-group">
                  <div className="form-control-wrap create-league-phone-field">
                    <div className="form-group">
                      <label className="form-label">Emergency Contact Number<span className="required-star">*</span></label>
                      <div className="phone">
                        <Controller
                          name="emergencyNo"
                          control={control}
                          render={({ field }) => (
                            <PhoneInput
                              country="in"
                              value={field.value ?? ''}
                              countryCodeEditable={false}
                              onChange={field.onChange}
                              inputClass={`form-control phone-input-field${errors.emergencyNo ? ' field-error' : ''}`}
                              containerClass="phone-input-container"
                              placeholder="Enter contact number"
                              enableSearch
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  {errors.emergencyNo?.message && <span className="error-message">{String(errors.emergencyNo.message)}</span>}
                </div>
 
                <div className="form-group">
                  <label className="form-label">Zip Code<span className="required-star">*</span></label>
                  <input className={`form-input ${errors.zipCode ? 'error' : ''}`} {...register('zipCode')} placeholder="Enter Zip Code" />
                  {errors.zipCode?.message && <span className="error-message">{String(errors.zipCode.message)}</span>}
                </div>
 
                <div className="form-group">
                  <label className="form-label">State<span className="required-star">*</span></label>
                  <input className={`form-input ${errors.state ? 'error' : ''}`} {...register('state')} placeholder="Enter State" />
                  {errors.state?.message && <span className="error-message">{String(errors.state.message)}</span>}
                </div>
 
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Address (Max 150 Characters)<span className="required-star">*</span></label>
                  <textarea className={`form-input ${errors.stAddress ? 'error' : ''}`} {...register('stAddress')} rows={2} placeholder="Enter Address" style={{ height: 'auto' }} />
                  {errors.stAddress?.message && <span className="error-message">{String(errors.stAddress.message)}</span>}
                </div>
 
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Description (Max 150 Characters)</label>
                  <textarea className="form-input" {...register('desc')} rows={2} placeholder="Enter Description" style={{ height: 'auto' }} />
                </div>
 
                <div className="form-group" style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '10px' }}>
                  <input type="checkbox" {...register('termsAccepted')} design-id="terms-checkbox" style={{ width: '20px', height: '20px', marginTop: '3px' }} />
                  <label className="form-label" style={{ fontSize: '13px', color: '#64748b' }}>
                    I accept CricCenter's Terms & Conditions, authorize the transfer of data from 3rd Party applications, and confirm that the information provided is accurate and complete.
                  </label>
                </div>
                {errors.termsAccepted?.message && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '-10px', gridColumn: 'span 2' }}>{String(errors.termsAccepted.message)}</div>}
              </div>
            )}
 
            <div className="action-buttons-footer" style={{ marginTop: '30px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
              {safeStepIndex === 0 ? (
                <button type="button" onClick={nextStep} className="btn-primary" style={{ width: '100%' }}>Continue</button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                    {safeStepIndex < steps.length - 1 ? (
                      <button type="button" onClick={nextStep} className="btn-primary" style={{ flex: 2 }}>Continue</button>
                    ) : (
                      <button 
                        type="button" 
                        onClick={async () => {
                          const fieldsToValidate: any[] = ['emergencyName', 'emergencyContactRelation', 'emergencyNo', 'zipCode', 'state', 'stAddress', 'termsAccepted'];
                          const isStepValid = await trigger(fieldsToValidate);
                          if (isStepValid) {
                            handleSubmit(onSubmit)();
                          } else {
                            toast.error('Please fill all required fields');
                          }
                        }} 
                        className="btn-primary" 
                        disabled={isSubmitting} 
                        style={{ flex: 2 }}
                      >
                        {isSubmitting ? 'Processing...' : 'Explore CricCenter'}
                      </button>
                    )}
                    {safeStepIndex < steps.length - 1 && (
                      <button type="button" onClick={handleCompleteLater} className="btn-secondary unfilled" style={{ flex: 2 }}>Complete Later</button>
                    )}
                  </div>
                </div>
              )}
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetup;
